/** 全局音乐律动设备列表：部分型号不可勾选（与 V1 下发无关，仅控制勾选） */

import {
  isBeiying916Or8815Device,
  supportsBeiying9168815MusicRhythm,
} from "../main/beiying9168815Rhythm";
import { isSdcxKeyboard } from "../main/sdcx/sdcxKeyboard";
import { SDCX_KEYBOARD_TYPES } from "../main/sdcx/sdcxKeyboardTypes";
import { isZhwxKeyboard } from "../main/zhwx/zhwxKeyboard";
import { ZHWX_KEYBOARD_TYPES } from "../main/zhwx/zhwxKeyboardTypes";
import {
  equipmentNameBlob,
  isM98GlobalRhythmSyncCapableEquipment,
  normalizeEquipmentName,
  resolveSupportedKeyboardTypeForEquipment,
} from "./supportedKeyboardMatch";

/** 任意连接方式均不可勾选（916/8815 有线已支持 setGifLight；智慧无限 / SDCX 全系暂不支持神光同步） */
const KEYBOARD_TYPES_ALWAYS_EXCLUDED_FROM_GLOBAL = new Set<string>([
  ...ZHWX_KEYBOARD_TYPES,
  ...SDCX_KEYBOARD_TYPES,
  /** 容园机械轴 X87S：暂不支持神光同步 / 全局音乐律动 */
  "rongyuan87",
]);

/** 仅 2.4G 时不可勾选（USB 可走全局；V1 仅在键盘子应用） */
const KEYBOARD_TYPES_EXCLUDED_ON_24G_FROM_GLOBAL = new Set([
  "75pro",
  "75protft",
  "m98",
  "dk108",
]);

export const GLOBAL_MUSIC_RHYTHM_UNSUPPORTED_KEYBOARD_TIP =
  "该型号暂不支持全局音乐律动";

export function canonicalKeyboardTypeForRhythmPolicy(
  keyboardType: unknown
): string {
  let n = String(keyboardType ?? "")
    .trim()
    .toLowerCase();
  if (/^x(?=[0-9a-z])/i.test(n)) {
    n = n.slice(1);
  }
  return n;
}

function readWorkModeFromDeviceInitialStatus(): string | number | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem("deviceInitialStatus");
    if (!raw) return undefined;
    const d = JSON.parse(raw) as { workMode?: string | number };
    return d?.workMode;
  } catch {
    return undefined;
  }
}

function resolveWorkMode(
  equipment: Record<string, unknown>
): string | number | undefined {
  const wm =
    equipment.workMode ?? (equipment as { work_mode?: unknown }).work_mode;
  if (wm !== undefined && wm !== null) {
    return wm as string | number;
  }
  return readWorkModeFromDeviceInitialStatus();
}

function is24GWorkMode(workMode: string | number | undefined): boolean {
  return String(workMode) === "2" || workMode === 2;
}

function isTripleHfdModelOn24G(
  keyboardType: unknown,
  displayname: unknown,
  workMode: string | number | undefined
): boolean {
  if (!is24GWorkMode(workMode)) return false;

  if (isM98GlobalRhythmSyncCapableEquipment({ keyboardType, displayname })) {
    return false;
  }

  const canonical = canonicalKeyboardTypeForRhythmPolicy(keyboardType);
  if (KEYBOARD_TYPES_EXCLUDED_ON_24G_FROM_GLOBAL.has(canonical)) {
    return true;
  }

  const dn = normalizeEquipmentName(displayname);
  return (
    dn === "x75 pro" ||
    dn === "x75 pro screen" ||
    dn === "m98" ||
    dn === "dk108"
  );
}

function isAlwaysExcludedKeyboardType(keyboardType: unknown): boolean {
  return KEYBOARD_TYPES_ALWAYS_EXCLUDED_FROM_GLOBAL.has(
    canonicalKeyboardTypeForRhythmPolicy(keyboardType)
  );
}

function isExcludedByEquipmentNameBlob(
  equipment: Record<string, unknown>
): boolean {
  const blob = equipmentNameBlob(equipment);
  if (!blob) return false;
  /** MUSE M80：仅 2.4G 不可勾选；有线走 916 setGifLight */
  if (/\bmuse\s*m80\b/.test(blob)) {
    return !supportsBeiying9168815MusicRhythm(equipment);
  }

  const workMode = resolveWorkMode(equipment);
  if (!is24GWorkMode(workMode)) return false;

  if (/x75\s*pro\s*screen|75\s*pro\s*screen/.test(blob)) return true;
  if (/x75\s*pro\b|75\s*pro\b/.test(blob) && !/screen/.test(blob)) {
    return true;
  }
  if (/\bdk108\b/.test(blob)) return true;
  if (/\bm98\b|muse-m98/.test(blob)) {
    if (isM98GlobalRhythmSyncCapableEquipment(equipment)) return false;
    return true;
  }

  return false;
}

export function isKeyboardTypeExcludedFromGlobalMusicRhythm(
  keyboardType: unknown
): boolean {
  return isAlwaysExcludedKeyboardType(keyboardType);
}

export function isEquipmentExcludedFromGlobalMusicRhythm(
  equipmentData: Record<string, unknown> | null | undefined
): boolean {
  if (!equipmentData || typeof equipmentData !== "object") return false;

  if (
    isBeiying916Or8815Device(equipmentData) &&
    !supportsBeiying9168815MusicRhythm(equipmentData)
  ) {
    return true;
  }

  /** 智慧无限（DK87 / DK75 Single 等）：方案级兜底，避免 keyboardType 缺失时误入神光同步 */
  if (isZhwxKeyboard(equipmentData)) {
    return true;
  }

  /** SDCX（DK63 Machine 等）：方案级兜底，暂不支持神光同步 */
  if (isSdcxKeyboard(equipmentData)) {
    return true;
  }

  if (isExcludedByEquipmentNameBlob(equipmentData)) {
    return true;
  }

  const workMode = resolveWorkMode(equipmentData);
  const displayname =
    equipmentData.displayname ??
    (equipmentData as { displayName?: unknown }).displayName;

  const kt =
    equipmentData.keyboardType ??
    (equipmentData as { keyboard_type?: unknown }).keyboard_type;
  if (isAlwaysExcludedKeyboardType(kt)) {
    return true;
  }
  if (isTripleHfdModelOn24G(kt, displayname, workMode)) {
    return true;
  }

  const resolved = resolveSupportedKeyboardTypeForEquipment(equipmentData);
  if (resolved && isAlwaysExcludedKeyboardType(resolved)) {
    return true;
  }
  if (resolved && isTripleHfdModelOn24G(resolved, displayname, workMode)) {
    return true;
  }

  return false;
}
